import { getPresetUrl, handleViewSterlingResults } from "@/utils/index"
import {
  getBadgesData,
  formatPrice,
  colorFetcheslogic,
} from "@/responseProcessors/plpResponseProcessor"
import { PlusIcon } from "@kohler-dsm-library/kohler-icons"
import {
  getPDPUrlWithConfig,
  sanitizeTextForAnalytics,
  getCurrentSkuCouponDiscounts,
  getSearchDataLayerDetails,
  isCanadaSite,
} from "@/utils/helper"
import { addToCompareProduct, removeFromCompareProduct } from "@/utils/product"
import MissingImage from "@/public/images/image-missing.png"
import {
  SEARCH_INTERNAL_TYPE,
  STOCKS,
  BRAND_NAME,
  BUNDLE_LABEL,
  STERLING_LINK,
  QUICK_VIEW,
  QUICK_VIEW_MODAL,
} from "@/constants/index"

const pushToAdobeDataLayer = (eventInfo, productInfo) => {
  const { adobeDataLayer: { getState } = {} } = window
  const page = getState?.("page") || {}
  window.adobeDataLayer.push({
    event: "cmp:click",
    eventInfo,
    ...(productInfo ? { productInfo } : {}),
    page,
  })
}
const isSkuDiscontinued = (skuDetails, sku) => {
  if (!Array.isArray(skuDetails)) return false

  const skuMatch = skuDetails.find(item => item.split("|")[2] === sku)

  if (!skuMatch) return false

  const discontinued = skuMatch.split("|")[4]
  return discontinued !== "false" || Date.parse(discontinued) < Date.now()
}
const calculatePrice = (
  data,
  persona,
  currencySign,
  colorSku,
  isSterling,
  isCanada,
  isBundle
) => {
  // for sterling products, GST price list is used
  const finalPrice = data?.[`priceList.${persona}.price_d`] || 0

  const skuDiscontinued = isSkuDiscontinued(
    data?.[`Color.SKU.Details_ss`],
    colorSku
  )
  const discountedPrice = skuDiscontinued
    ? null
    : isSterling
    ? data?.[`priceStartingAt.GST_d`] // Sterling (any country)
    : isCanada
    ? data?.[`priceList.${persona}.price_d`] // Non-Sterling + Canada
    : data?.[`priceList.${persona}.finalPrice_d`] // Non-Sterling + Non-Canada

  const colorPriceList = data?.[`Color.${persona}.Details_ss`] || 0

  const getPriceDetails = (gstDetails, colorSKU) => {
    if (!Array.isArray(gstDetails)) return null

    const match = gstDetails.find(item => item.startsWith(colorSKU + "|"))
    if (!match) return null

    const parts = match.split("|")
    const displayPrice = parseFloat(parts[3])
    const slashedPrice = parseFloat(parts[2])

    return { displayPrice, slashedPrice }
  }

  // onHover color price logic
  if (colorSku !== data.sku_s && Array.isArray(colorPriceList)) {
    const colorPriceData = getPriceDetails(colorPriceList, colorSku)

    if (colorPriceData) {
      const { displayPrice, slashedPrice } = colorPriceData
      if (isCanada) {
        return {
          displayPrice: formatPrice(slashedPrice, currencySign),
        }
      }
      return {
        displayPrice: formatPrice(displayPrice, currencySign),
        slashedPrice:
          slashedPrice && slashedPrice !== displayPrice
            ? formatPrice(slashedPrice, currencySign)
            : null,
      }
    }
  }
  if (isBundle && persona) {
    const bundlePriceKey = `priceStartingAt.${persona}_d`
    const bundlePrice = data?.[bundlePriceKey]
    if (bundlePrice) {
      return { displayPrice: `Starting at ${currencySign}${bundlePrice}` }
    } else {
      // Fallback to min_price if persona-specific price is not available
      const fallbackPrice = data?.["min_price"]
      if (fallbackPrice) {
        return { displayPrice: `Starting at ${currencySign}${fallbackPrice}` }
      }
    }
  }

  // Normal price logic
  if (discountedPrice > 0 && discountedPrice !== finalPrice) {
    return {
      displayPrice: formatPrice(discountedPrice, currencySign),
      slashedPrice: formatPrice(finalPrice, currencySign),
    }
  } else {
    return {
      displayPrice: formatPrice(discountedPrice, currencySign),
      slashedPrice: null,
    }
  }
}

const handleCompare = (
  e,
  comparedProducts,
  category,
  data,
  setCompareErrorMessage,
  setCompareChecked,
  t
) => {
  // ✅ Don't update state immediately - validate first

  if (e.target.checked) {
    let variantFlag = false
    if (comparedProducts.length < 4) {
      const allVariantSkus = comparedProducts
        ?.map(a => {
          const variantSkus = a["variantList.sku_ss"] || []
          return [...variantSkus, a.masterSKU_s || a.sku_s].filter(Boolean)
        })
        .flat()

      if (allVariantSkus?.includes(data.sku_s)) {
        variantFlag = true
      }
      if (category !== "" && category !== data.Product_Category) {
        setCompareErrorMessage(t("kf.compare.validation.sameCategory"))
        setCompareChecked(false) // ✅ Only set false on validation failure
        removeFromCompareProduct(data.sku_s)
      } else {
        addToCompareProduct(data.sku_s, data)
        setCompareChecked(true) // ✅ Only set true when validation passes
      }
    } else if (
      variantFlag &&
      process.env.NEXT_PUBLIC_COMPARE_PRODUCTS_VERSION === "compare_products_v2"
    ) {
      setCompareErrorMessage(t("kf.compare.validation.sameVariants"))
      setCompareChecked(false) // ✅ Set false on validation failure
      removeFromCompareProduct(data.sku_s)
    } else {
      setCompareErrorMessage(t("kf.plp.error.maxProduct"))
      setCompareChecked(false) // ✅ Set false when max products reached
      removeFromCompareProduct(data.sku_s)
    }
  } else {
    setCompareChecked(false) // ✅ Set false when unchecking
    const isCurrentlyCompared = comparedProducts.some(
      prod => prod.compareSku === data.sku_s
    )
    if (isCurrentlyCompared) {
      removeFromCompareProduct(data.sku_s)
    }
  }
}

const compareColorData = (gstDetails, skuDetails, isSterling) => {
  // If persona-specific details (e.g. Color.GST.Details_ss) are absent from the
  // LW response, fall back directly to Color.SKU.Details_ss which has the same
  // pipe-delimited format and contains all the data colorFetcheslogic needs.
  if (!gstDetails.length) {
    return skuDetails
  }
  // Step 1: Extract SKUs from Color.GST.Details_ss
  const gstSKUs = new Set(gstDetails.map(item => item.split("|")[0]))

  // Step 2: Filter Color.SKU.Details_ss entries where SKU is in GST list
  const matched = skuDetails.filter(item => {
    const sku = item.split("|")[2]
    return gstSKUs.has(sku)
  })

  const uniqueColors = []
  const seenColors = new Set()

  for (const item of matched) {
    const colorName = item.split("|")[0]

    if (!seenColors.has(colorName)) {
      seenColors.add(colorName)
      uniqueColors.push(item)
    }
  }

  // Step 3: Return the matched entries
  return !isSterling ? uniqueColors : skuDetails
}

export const getProductInfo = ({
  product,
  isSterling,
  siteName,
  colorSku,
  skuPrepend,
  cartData,
  locale,
  type,
  isCanada,
}) => {
  const { productCouponCode, productCoupondiscount } =
    getCurrentSkuCouponDiscounts({
      cart: cartData,
      sku: product.sku_s,
      locale: locale,
    })
  const getGlobalPromotionPrice = (price, discountPercent) => {
    const discountAmount = Number((price - discountPercent).toFixed(2))
    // If there is a discount, return the price minus the discount amount (rounded to 2 decimals)
    if (discountPercent && discountAmount) {
      return Number((price - discountAmount).toFixed(2))
    }
    // Otherwise, return the regular price
    return price
  }
  const getDiscountPriceState = (
    badgeVal,
    isDiscountedPrice,
    discountPercent
  ) => {
    return badgeVal && isDiscountedPrice
      ? "percentOff|" + discountPercent
      : "regular price"
  }
  const getColorName = (color, colorSku) => {
    return (
      color.find(item => item.split("|")[2] === colorSku)?.split("|")[0] ?? ""
    )
  }
  const getDefaultImage = () => {
    return !isSterling
      ? product?.["productImages.labelWithUrl_ss"]
          ?.find(img => product.sku_s === img.split("|")[0])
          ?.split("|")[1]
      : "MissingImage"
  }
  const getProductSalePrice = () => {
    return !isSterling
      ? product?.variantPrice
          ?.find(img => colorSku === img.split("|")[0])
          ?.split("|")[3]
      : product?.discountedPrice
  }
  const getProductInStock = (color, colorSku) => {
    return color
      .find(item => item.split("|")[2] === colorSku)
      ?.split("|")[3] === "true"
      ? STOCKS.IN_STOCK
      : STOCKS.OUT_OF_STOCK
  }

  // Product Info Object
  const productInfo = {
    productBrand: isSterling
      ? BRAND_NAME.sterling.toLowerCase()
      : sanitizeTextForAnalytics(siteName?.toLowerCase()),
    description:
      sanitizeTextForAnalytics(
        type?.toLowerCase() === "parts"
          ? product?.productName_s
          : isSterling && isCanada
          ? product?.ProductDescriptionProduct_s
          : product?.ProductDescriptionProductShort_s
      ) || "n/a",
    globalPromotionPrice: getGlobalPromotionPrice(
      product.price,
      product.discountPercent
    ),
    priceState: getDiscountPriceState(
      product.badgeValue,
      product.isDiscountedPrice,
      product.discountPercent
    ),
    productBasePrice: Number(product?.price),
    productCategory: sanitizeTextForAnalytics(product?.category) || "n/a",
    productColor:
      sanitizeTextForAnalytics(getColorName(product.colors, colorSku)) || "n/a",
    productID: skuPrepend + colorSku,
    productName:
      sanitizeTextForAnalytics(
        isSterling
          ? product.ProductBrandName_s
          : type?.toLowerCase() === "parts"
          ? product.productName_s
          : product.RegionBrandName_s || product.ProductBrandName_s
      ) || "n/a",
    productRoom: sanitizeTextForAnalytics(
      product?.["category1_s"] === "n/a"
        ? product?.category
        : product?.["category1_s"]
    ),
    productSalePrice: Number(getProductSalePrice()) || 0,
    productSaleable: !product.colors.length ? false : true,
    productStatus: getProductInStock(product.colors, colorSku) || "n/a",
    productSuperSku: product.isSuperSKU,
    productTileName: sanitizeTextForAnalytics(product.Tile_Title) || "n/a",
    defaultImageName: getDefaultImage() || "MissingImage",
    productCollectionsName: sanitizeTextForAnalytics(
      product.category || product?.["ProductBrandNameDisplay_s"]
    ),
    globalOffer: product.badgeValue ? product.badgeValue : "n/a",
    globalDiscount: product.discountPercent ? product.discountAmount : 0,
    productPartnerBuyNow: product?.["ProductIsRetail"] ?? false,
    pdpType: product?.primaryBundleSKU_s
      ? BUNDLE_LABEL.toLowerCase()
      : "regular finished goods & service parts",
    productFindingMethod: "n/a",
    productCouponCode: productCouponCode.join("|") || "n/a",
    productCoupondiscount: productCoupondiscount,
    frequency: "n/a",
    isRecommended: "n/a",
    isSendNow: "n/a",
    isSubscription: "n/a",
    quantity: "n/a",
    ratings: "n/a",
    numberOfReviews: "n/a",
  }
  return productInfo
}

export const getEventInfo = ({
  isSterling,
  product,
  index,
  pdpURL,
  searchResultDataLayerObj,
  clickEvent,
  compareChecked,
  favIconState,
}) => {
  let alterNameType = ""
  let compareInternalLinkName = ""
  let compareLinkType = ""
  let compareZoneName = ""
  if (clickEvent === "compare_click") {
    alterNameType = compareChecked ? "compare remove" : "compare add"
    compareInternalLinkName = compareChecked
      ? "remove compare"
      : "add to compare"
    compareLinkType = "compare"
    compareZoneName = "compare"
  } else if (clickEvent === "favorites") {
    if (favIconState) {
      alterNameType = "favorite remove"
    } else {
      alterNameType = "favorite add"
    }
  } else if (clickEvent === "service_parts_click") {
    alterNameType = "parts diagram"
    compareZoneName = "parts diagram"
    compareLinkType = "parts diagram"
  } else if (clickEvent === "quick_view_click") {
    compareZoneName = `${QUICK_VIEW}`
    compareLinkType = `${QUICK_VIEW}`
  } else if (clickEvent === "quick_view_close") {
    compareZoneName = `${QUICK_VIEW}:close`
    compareLinkType = `${QUICK_VIEW}:close`
  } else {
    alterNameType = "navigation"
    compareLinkType = "product tiles"
    compareZoneName = "products"
  }
  const productName = isSterling
    ? product.ProductBrandName_s
    : product.RegionBrandName_s || product.ProductBrandName_s
  const eventInfo = {
    eventAction:
      clickEvent === "quick_view_click"
        ? `search results:${QUICK_VIEW}`
        : clickEvent === "quick_view_close"
        ? "search results:quick view modal:close"
        : `search results product ${alterNameType}`,
    eventName:
      clickEvent === "quick_view_click"
        ? `search results:${QUICK_VIEW}`
        : clickEvent === "quick_view_close"
        ? "search results:quick view modal:close"
        : `search results product ${alterNameType}`,
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    productGridPosition: index + 1,
    internalLinkName: isSterling
      ? SEARCH_INTERNAL_TYPE.sterlingResults
      : clickEvent === "swatch_click"
      ? "color selection"
      : clickEvent === "compare_click"
      ? compareInternalLinkName
      : clickEvent === "service_parts_click"
      ? "service parts"
      : clickEvent === "quick_view_click"
      ? `${QUICK_VIEW}`
      : clickEvent === "quick_view_close"
      ? "close"
      : sanitizeTextForAnalytics(productName),
    internalLinkPosition: "search results",
    internalLinkType: `search results:${compareLinkType}`,
    internalLinkZoneName: `search results:${compareZoneName}:main content area`,
    internalLinkURL: isSterling
      ? window.location.href
      : clickEvent === "compare_click"
      ? "n/a"
      : clickEvent === "service_parts_click"
      ? "n/a"
      : clickEvent === "quick_view_click"
      ? "n/a"
      : clickEvent === "quick_view_close"
      ? "n/a"
      : window.location.origin + pdpURL(product.sku_s),
    clickInternalLinks: "true",

    ...(clickEvent !== "favorites" &&
    clickEvent !== "service_parts_click" &&
    clickEvent !== "quick_view_click" &&
    clickEvent !== "quick_view_close"
      ? getSearchDataLayerDetails(searchResultDataLayerObj)
      : {}),
    ...((clickEvent === "quick_view_click" ||
      clickEvent === "quick_view_close") && {
      modalName: QUICK_VIEW_MODAL,
    }),
  }
  return eventInfo
}

// Main function to get Product Tab Data
export const getProductTabData = ({
  data,
  isSterling,
  globalConfig,
  persona,
  handleOnClickPartsDiagramLink,
  compareData,
  compareErrorMessage,
  setCompareErrorMessage,
  compareChecked,
  setCompareChecked,
  favIconState,
  handleFavorite,
  swatchCount,
  setColorSku,
  colorSku,
  index,
  cartData,
  locale,
  searchResultDataLayerObj,
  brandSwitcherItems,
  type,
  width,
  paginationState,
  setCardLoader,
  setShowQuickViewModal,
  setScrollY,
  t,
  saveButtonRef,
  handleSaveButtonFocus,
}) => {
  const currentRows =
    Number(paginationState.curRows ?? 0) + Number(paginationState.start ?? 0)
  const { comparedProducts, category } = compareData

  const {
    general: { swatchUrl = "", disableCommerce, siteName } = {},
    presetConfigs: { plp: plpPresetConfig } = {},
    plpConfigurations: { myArrayList: plpBadgeList = [] } = {},
    internationalization: { currencySign } = {},
    apiEndpoints: { skuPrepend = "" } = {},
    general = {},
    plpServletPath = {},
  } = globalConfig

  const product = {
    ...data,
    shipmentDate: data?.["RegionReleaseforShipment_dt"] ?? "",
    badgeValue: data?.[`priceList.${persona}.saleOffer_s`] ?? "",
    price: data?.[`priceStartingAt.${persona}_d`] ?? 0,
    discountedPrice: data?.[`priceList.${persona}.finalPrice_d`] ?? 0,
    isDiscountedPrice: data?.[`priceList.${persona}.discountedPrice_d`],
    discountPercent:
      data?.[`priceList.${persona}.saleOffer_s`] &&
      data?.[`priceList.${persona}.discountedPrice_d`]
        ? 100 -
          Math.round(
            (data?.[`priceList.${persona}.discountedPrice_d`] /
              data?.[`priceStartingAt.${persona}_d`]) *
              100
          )
        : 0,
    resourceNvznId: data?.["ResourceNvznId_s"],
    resourceVntanaProductId: data?.["ResourceVntanaProductId_s"],
    resourceVntanaProductIdList: data?.["ResourceVntanaProductIdList_ss"],
    resourceNvznIdList: data?.["ResourceNvznIdList_ss"],
    category:
      data?.["Product_Category"] ?? data?.["ProductLocalCategory_s"] ?? "",
    slug: data?.["slug_s"],
    sku: data?.["sku_s"],
    variantPrice: data?.[`Color.${persona}.Details_ss`],
    colors: compareColorData(
      data?.[`Color.${persona}.Details_ss`] || [],
      data?.[`Color.SKU.Details_ss`] || [],
      isSterling
    ),
    isSuperSKU: data?.["comboSKUAvailable_b"] || false,
    isBundleProduct: Boolean(data?.primaryBundleSKU_s),
  }

  const bundleFlag = data?.Bundle_s
  const isBundle = bundleFlag === "Yes"

  const configdata = {
    plpBadgeList,
    disableCommerce,
  }
  const badgesdata = getBadgesData(product, configdata) || []
  const isCanada = isCanadaSite(siteName)
  const isRetailProduct =
    data?.["ProductIsRetail"] === "true" ||
    data?.["ProductIsRetail"] === true ||
    data?.["ProductIsRetail_s"] === "true" ||
    data?.["ProductIsRetail_s"] === true
  const { displayPrice, slashedPrice } = calculatePrice(
    data,
    persona,
    currencySign,
    colorSku,
    isSterling,
    isCanada,
    isBundle
  )
  const skuForBundle =
    isBundle && data?.["variantList.sku_ss"]?.[0]
      ? data?.["variantList.sku_ss"]?.[0]
      : ""

  const pdpURL = pSku => {
    const skuToUse = isBundle ? skuForBundle || pSku : pSku

    return `${getPDPUrlWithConfig(
      product.category,
      product.slug,
      plpServletPath,
      general
    )}?skuId=${skuToUse}`
  }

  const productInfo = getProductInfo({
    product,
    isSterling,
    siteName,
    colorSku,
    skuPrepend,
    cartData,
    locale,
    type,
    isCanada,
  })

  const getAnalyticsInfoOnclick = (eventType = "", status, message) => {
    const eventInfo = getEventInfo({
      isSterling,
      product,
      index,
      pdpURL,
      searchResultDataLayerObj,
      clickEvent: eventType,
      compareChecked,
      favIconState,
    })
    eventInfo.eventStatus = status || "n/a"
    eventInfo.eventMsg = sanitizeTextForAnalytics(message) || "n/a"
    pushToAdobeDataLayer(eventInfo, productInfo)
  }

  const setSessionStorageForPLP = pSku => {
    sessionStorage.setItem("plpScrollSku", "K-" + pSku) // back=K-T13140-4A-CP
    sessionStorage.setItem("plpCurrentRows", currentRows)
    setCardLoader(true)
  }

  const allColorsData = colorFetcheslogic(product, width, swatchUrl)
  const colorsData = allColorsData.slice(0, swatchCount) || []
  const colorsDataWithEvent = colorsData.map(color => ({
    ...color,
    ...(!isSterling && { component: "a", href: pdpURL(color.sku) }),
    onMouseEnter: () => {
      setColorSku(color.sku)
    },
    onMouseLeave: () => {
      setColorSku(data.sku_s)
    },
  }))

  // quick view modal open
  const handleQuickViewCTAClick = (e, sku) => {
    const scrollY = window?.scrollY
    setScrollY(scrollY)
    setShowQuickViewModal(true)
    sessionStorage.setItem("plpScrollSku", "K-" + sku) // back=K-T13140-4A-CP
    sessionStorage.setItem("plpCurrentRows", currentRows)
  }

  const variantImages =
    !isSterling &&
    data?.["productImages.labelWithUrl_ss"]
      ?.find(img => colorSku === img.split("|")[0])
      ?.split("|")[1]

  const getBundleImageUrl = () => {
    // Step 1: If we have a direct bundleImageUrl, return it directly
    if (data?.["BundlePLPImageURL_s"]) {
      return data?.["BundlePLPImageURL_s"]
    }

    // Step 2: If we have variantImages, use getPresetUrl with it
    if (variantImages) {
      return getPresetUrl(
        width,
        width <= 1024 ? 2 : 3,
        swatchUrl,
        variantImages,
        plpPresetConfig,
        false
      )
    }

    // Step 3: Find the bundle image URL and use getPresetUrl
    const bundleImageUrl = data?.["productImages.labelWithUrl_ss"]
      ?.find(img => skuForBundle === img.split("|")[0])
      ?.split("|")[1]

    return getPresetUrl(
      width,
      width <= 1024 ? 2 : 3,
      swatchUrl,
      bundleImageUrl,
      plpPresetConfig,
      false
    )
  }

  const isDiscontinuedProductCompareCheck = () => {
    return Date.parse(product?.["RegionDiscontinuedDate_dt"]) < Date.now()
  }
  // Main props to be returned - Product Card props
  const props = {
    type: "Product Card",
    mod: "white",
    id: `${data.sku_s}`,
    key: `${skuPrepend}${data.sku_s}`,
    ...(!isSterling &&
      width > 1023 &&
      data?.["productDiscontinued_b"] !== true &&
      !isCanada &&
      !data?.sku_s?.includes("BNDL") &&
      data?.["ProductIsRetail_s"] !== "true" &&
      data?.["SKUIsProjectPack_s"] !== "Yes" && {
        quickViewCTA: {
          label: t("kf.plp.product.quick.view.label"),
          "aria-label": `Open Quick View for ${data?.ProductBrandName_s}`,
          icon: PlusIcon,
          onClick: e => {
            handleQuickViewCTAClick(e, data?.sku_s)
            getAnalyticsInfoOnclick("quick_view_click")
          },
        },
      }),
    ...(!isSterling && {
      href: pdpURL(data.sku_s),
    }),
    image: {
      id: `IMG_${data.sku_s}_${index}`,
      reveal: "default",
      loading: "blank",
      src: isSterling
        ? data?.["productImage.url_ss"][0]
        : isBundle
        ? getBundleImageUrl()
        : getPresetUrl(
            width,
            width <= 1024 ? 2 : 3,
            swatchUrl,
            variantImages,
            plpPresetConfig,
            false
          ),
      alt: data.metaTitle_s,
      onError: e => {
        e.currentTarget.onerror = null
        e.currentTarget.src = MissingImage.src
      },
      ...(!isSterling
        ? {
            onClick: () => {
              getAnalyticsInfoOnclick()
              setSessionStorageForPLP(data.sku_s)
            },
          }
        : {
            onClick: () => {
              handleViewSterlingResults(
                !isCanada ? brandSwitcherItems : STERLING_LINK,
                data?.Brand_ss[0],
                data.sku_s
              )
              getAnalyticsInfoOnclick("sterling_view_results")
            },
          }),
    },
    description: {
      text: isBundle
        ? data?.Collapse_Description_s
        : isSterling && isCanada
        ? product?.ProductDescriptionProduct_s
        : data?.Collapse_Description_s ??
          data?.ProductDescriptionProductShort_s,
      "aria-label": data?.ProductDescriptionProductShort_s,
      ...(!isSterling
        ? {
            component: "a",
            href: pdpURL(data.sku_s),
            onClick: () => {
              getAnalyticsInfoOnclick()
              setSessionStorageForPLP(data.sku_s)
            },
          }
        : {
            onClick: () => {
              handleViewSterlingResults(
                !isCanada ? brandSwitcherItems : STERLING_LINK,
                data?.Brand_ss[0],
                data.sku_s
              )
              getAnalyticsInfoOnclick("sterling_view_results")
            },
          }),
    },
    title: {
      text: isBundle
        ? data?.Collapse_Title_s
        : isSterling
        ? data.ProductBrandName_s
        : type?.toLowerCase() === "parts"
        ? data.productName_s
        : data.RegionBrandName_s || data.productName_s,
      "aria-label": isSterling
        ? data.ProductBrandName_s
        : data.RegionBrandName_s || data.ProductBrandName_s,
      ...(!isSterling
        ? {
            component: "a",
            href: pdpURL(data.sku_s),
            onClick: () => {
              getAnalyticsInfoOnclick()
              setSessionStorageForPLP(data.sku_s)
            },
          }
        : {
            onClick: () => {
              handleViewSterlingResults(
                !isCanada ? brandSwitcherItems : STERLING_LINK,
                data?.Brand_ss[0],
                data.sku_s
              )
              getAnalyticsInfoOnclick("sterling_view_results")
            },
          }),
    },
    ...(!isRetailProduct && {
      price: {
        text: displayPrice,
        "aria-label": `${t("kf.plp.label.salePriceAria")} ${displayPrice ?? 0}`,
        ...(!isSterling
          ? {
              component: "a",
              href: pdpURL(data.sku_s),
              onClick: () => {
                getAnalyticsInfoOnclick()
                setSessionStorageForPLP(data.sku_s)
              },
            }
          : {
              onClick: () => {
                handleViewSterlingResults(
                  !isCanada ? brandSwitcherItems : STERLING_LINK,
                  data?.Brand_ss[0],
                  data.sku_s
                )
                getAnalyticsInfoOnclick("sterling_view_results")
              },
            }),
      },
      slashedPrice: {
        text: slashedPrice,
        "aria-label": `${t("kf.plp.label.originalPriceAria")} ${
          slashedPrice ?? 0
        }`,
        ...(!isSterling && {
          component: "a",
          href: pdpURL(data.sku_s),
          onClick: () => {
            getAnalyticsInfoOnclick()
            setSessionStorageForPLP(data.sku_s)
          },
        }),
      },
    }),

    ...(!isSterling &&
      !isBundle &&
      product.category !== "Install Services" && {
        isSaved: favIconState,
        saveCTA: {
          ref: saveButtonRef,
          onFocus: handleSaveButtonFocus,
          "aria-label": `Add ${data.ProductBrandName_s} to list`,
          onClick: e => {
            handleFavorite(e, data.sku_s, productInfo, getAnalyticsInfoOnclick)
          },
        },
      }),

    productDetails: {
      SKU: {
        text: isBundle ? skuForBundle : skuPrepend + colorSku,
        ...(!isSterling
          ? {
              component: "a",
              href: pdpURL(data.sku_s),
              onClick: () => {
                getAnalyticsInfoOnclick()
                setSessionStorageForPLP(data.sku_s)
              },
            }
          : {
              onClick: () => {
                handleViewSterlingResults(
                  !isCanada ? brandSwitcherItems : STERLING_LINK,
                  data?.Brand_ss[0],
                  data.sku_s
                )
                getAnalyticsInfoOnclick("sterling_view_results")
              },
            }),
      },
      tags: badgesdata,
      ...(!isBundle && {
        additionalLink: {
          "aria-label": `See Service Parts for ${data.ProductBrandName_s}`,
          text:
            data?.["ProductProductNo_s"] &&
            data?.["ProductResource.InteractiveSVG.resourceName_ss"]
              ? t("kf.search.serviceparts")
              : "",
          onClick: () => {
            handleOnClickPartsDiagramLink(product)
            getAnalyticsInfoOnclick("service_parts_click")
          },
        },
      }),
      ...(!isSterling &&
        !isBundle &&
        !data?.primaryBundleSKU_s &&
        product.category !== "Install Services" &&
        !isDiscontinuedProductCompareCheck() &&
        width > 1023 && {
          checkbox: {
            label: t("kf.compare.label.compare"),
            "aria-label": `Add ${data.ProductBrandName_s} to compare rack.`,
            checked: compareChecked,
            onChange: e => {
              handleCompare(
                e,
                comparedProducts,
                category,
                data,
                setCompareErrorMessage,
                setCompareChecked,
                t
              )
              getAnalyticsInfoOnclick("compare_click")
            },
            ...(compareErrorMessage && {
              tooltip: {
                label: compareErrorMessage,
                width: "200px",
              },
            }),
          },
        }),
      ...(!isBundle && {
        colorSwatches: {
          "aria-label": `${colorsDataWithEvent.length}${t(
            "kf.plp.label.colorsLinkAria"
          )}`,
          colors: colorsDataWithEvent,
          extraColors:
            allColorsData.length > swatchCount
              ? allColorsData.length - swatchCount
              : "",
          ...(colorSku !== data.sku_s && { selectedColor: colorSku }),
          ...(!isSterling
            ? {
                onColorSelected: () => {
                  getAnalyticsInfoOnclick("swatch_click")
                  setSessionStorageForPLP(data.sku_s)
                },
              }
            : {
                onClick: () => {
                  handleViewSterlingResults(
                    !isCanada ? brandSwitcherItems : STERLING_LINK,
                    data?.Brand_ss[0],
                    colorSku
                  )
                  getAnalyticsInfoOnclick("sterling_view_results")
                },
              }),
        },
      }),
    },

    height: "100%",
  }
  const sterlingButtonProps = {
    mod: "white",
    children: t("kf.viewSterlingResults"),
    variant: "primary",
    size: "small",
    fullWidth: true,
    disabled: false,
    onClick: () => {
      handleViewSterlingResults(
        !isCanada ? brandSwitcherItems : STERLING_LINK,
        data?.Brand_ss[0],
        data.sku_s
      )
      getAnalyticsInfoOnclick("sterling_view_results")
    },
  }
  return {
    productTabProps: props,
    sterlingButtonProps,
    getAnalyticsInfoOnclick,
    productInfo,
  }
}
